import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Form } from "react-bootstrap";
import DualRangeSlider from "./DualRangeSlider";

interface DurationFilterProps {
  onFilterChange?: (minDuration: number, maxDuration: number) => void;
}

export interface DurationFilterRef {
  reset: () => void;
}

const DurationFilter = forwardRef<DurationFilterRef, DurationFilterProps>(({ onFilterChange }, ref) => {
  const MIN_DURATION = 0;
  const MAX_DURATION = 300;
  const STEP = 5;
  
  const [minDuration, setMinDuration] = useState<number>(MIN_DURATION);
  const [maxDuration, setMaxDuration] = useState<number>(MAX_DURATION);
  const isInitialMount = useRef(true);

  // Expose reset function to parent component
  useImperativeHandle(ref, () => ({
    reset: () => {
      setMinDuration(MIN_DURATION);
      setMaxDuration(MAX_DURATION);
      if (onFilterChange) {
        onFilterChange(MIN_DURATION, MAX_DURATION);
      }
    }
  }));

  useEffect(() => {
    // Skip the effect on initial mount to prevent infinite loops
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Notify parent component when filter values change
    if (onFilterChange) {
      onFilterChange(minDuration, maxDuration);
    }
  }, [minDuration, maxDuration, onFilterChange]);

  const handleRangeChange = (min: number, max: number) => {
    setMinDuration(min);
    setMaxDuration(max);
  };

  return (
    <div className="duration-filter">
      <Form.Group>
        <DualRangeSlider
          min={MIN_DURATION}
          max={MAX_DURATION}
          step={STEP}
          unit="min"
          initialMin={minDuration}
          initialMax={maxDuration}
          onChange={handleRangeChange}
        />
      </Form.Group>
    </div>
  );
});

export default DurationFilter;
